import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FolderKanban,
  Loader2,
  Link2,
  Trash2,
  Save,
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import {
  normalizeProjectLink,
  PROJECT_PROCESSING_STATUSES,
  type ProjectConversation,
  type ProjectDoc,
  type ProjectItem,
  validateProjectLink,
} from '@/pages/project/project-utils'
import { ProjectDocumentsSection } from '@/pages/project/ProjectDocumentsSection'
import { ProjectChatsSection } from '@/pages/project/ProjectChatsSection'

const LAST_CONV_KEY = 'miniapp_last_conversation_id'
const LAST_SPACE_KEY = 'miniapp_last_space_id'
const LAST_CONTEXT_MODE_KEY = 'miniapp_last_context_mode'

export default function ProjectPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const projectId = String(id || '').trim()

  const [project, setProject] = useState<ProjectItem | null>(null)
  const [conversations, setConversations] = useState<ProjectConversation[]>([])
  const [loading, setLoading] = useState(true)
  const [savingTools, setSavingTools] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [contextMode, setContextMode] = useState<'isolated' | 'space_shared'>('space_shared')

  const [notesDraft, setNotesDraft] = useState('')
  const [instructionsDraft, setInstructionsDraft] = useState('')
  const [tasksDraft, setTasksDraft] = useState('')
  const [links, setLinks] = useState<string[]>([])
  const [linkInput, setLinkInput] = useState('')
  const [linkInputError, setLinkInputError] = useState<string | null>(null)
  const [toolsUpdatedAt, setToolsUpdatedAt] = useState<string | null>(null)

  const [docs, setDocs] = useState<ProjectDoc[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsUploading, setDocsUploading] = useState(false)
  const [docsError, setDocsError] = useState<string | null>(null)
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [acceptAttr, setAcceptAttr] = useState('.pdf,.docx,.txt')
  const [maxUploadMb, setMaxUploadMb] = useState(25)
  const uploadInputRef = useRef<HTMLInputElement>(null)

  const loadProjectDocs = useCallback(async () => {
    if (!projectId) return
    setDocsLoading(true)
    setDocsError(null)
    try {
      const res = await apiClient.getDocuments({
        contractId: projectId,
        page: 1,
        pageSize: 200,
      })
      const items = Array.isArray(res.items) ? res.items : []
      setDocs(
        items
          .filter((item) => String(item.contract_id || '').trim() === projectId)
          .map((item) => ({
            id: String(item.id || '').trim(),
            filename: String(item.filename || '').trim(),
            contract_id: item.contract_id || null,
            scope: item.scope,
            size_bytes: item.size_bytes,
            status: item.status,
            error_message: item.error_message || null,
            created_at: item.created_at,
          }))
          .filter((item) => Boolean(item.id)),
      )
    } catch (err) {
      setDocs([])
      setDocsError(err instanceof Error ? err.message : 'Не удалось загрузить документы проекта')
    } finally {
      setDocsLoading(false)
    }
  }, [projectId])

  const loadProjectContext = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const [spacesData, toolsData, convData] = await Promise.all([
        apiClient.getSpaces(200),
        apiClient.getProjectTools(projectId),
        apiClient.getConversations(100, projectId),
      ])

      const items = Array.isArray(spacesData.items) ? spacesData.items : []
      const found = items.find((item) => item.id === projectId)
      if (!found) {
        setProject(null)
      } else {
        setProject({
          id: found.id,
          name: found.name,
          description: found.description,
          is_personal: found.is_personal,
          updated_at: found.updated_at,
          created_at: found.created_at,
        })
      }

      const convItems = Array.isArray(convData.items) ? convData.items : []
      setConversations(
        convItems.map((item) => ({
          id: String(item.id || '').trim(),
          title: String(item.title || 'Новый чат'),
          last_message: item.last_message || null,
          updated_at: item.updated_at,
          message_count: Number(item.message_count || 0),
        })).filter((item) => Boolean(item.id)),
      )

      const projectTools = toolsData || { links: [], instructions: '', tasks: [], notes: '' }
      const nextLinks = Array.isArray(projectTools.links)
        ? projectTools.links.map((item) => String(item || '').trim()).filter(Boolean)
        : []
      setLinks(nextLinks)
      setInstructionsDraft(String(projectTools.instructions || ''))
      setNotesDraft(String(projectTools.notes || ''))
      setTasksDraft(
        Array.isArray(projectTools.tasks)
          ? projectTools.tasks.map((item) => String(item || '').trim()).filter(Boolean).join('\n')
          : '',
      )
      setToolsUpdatedAt(projectTools.updated_at || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки данных проекта')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const capabilities = await apiClient.getDocumentsCapabilities()
        const item = capabilities?.item || {}
        if (Array.isArray(item.allowed_extensions) && item.allowed_extensions.length > 0) {
          setAcceptAttr(item.allowed_extensions.join(','))
        }
        if (typeof item.max_upload_mb === 'number') {
          setMaxUploadMb(item.max_upload_mb)
        }
      } catch {
        // keep defaults
      }
    }
    void loadCapabilities()
  }, [])

  useEffect(() => {
    void loadProjectContext()
    void loadProjectDocs()
  }, [loadProjectContext, loadProjectDocs])

  const hasProcessingDocs = useMemo(
    () => docs.some((doc) => PROJECT_PROCESSING_STATUSES.has(String(doc.status || '').toLowerCase())),
    [docs],
  )

  useEffect(() => {
    if (!hasProcessingDocs && !activeJobId) return
    const timer = window.setInterval(() => {
      void loadProjectDocs()
    }, 2500)
    return () => window.clearInterval(timer)
  }, [activeJobId, hasProcessingDocs, loadProjectDocs])

  useEffect(() => {
    if (!activeJobId) return
    const timer = window.setInterval(async () => {
      try {
        const job = await apiClient.getDocumentsJob(activeJobId)
        const status = String(job?.item?.status || '').toLowerCase()
        await loadProjectDocs()
        if (status === 'completed' || status === 'failed') {
          setActiveJobId(null)
        }
      } catch {
        await loadProjectDocs()
      }
    }, 2000)
    return () => window.clearInterval(timer)
  }, [activeJobId, loadProjectDocs])

  const openProjectChat = (conversationId?: string) => {
    try {
      localStorage.setItem(LAST_SPACE_KEY, projectId)
      localStorage.setItem(LAST_CONTEXT_MODE_KEY, contextMode)
      if (conversationId) {
        localStorage.setItem(LAST_CONV_KEY, conversationId)
      } else {
        localStorage.removeItem(LAST_CONV_KEY)
      }
    } catch {
      // ignore
    }
    navigate('/chat')
  }

  const handleAddLink = () => {
    const validateError = validateProjectLink(linkInput)
    if (validateError) {
      setLinkInputError(validateError)
      return
    }
    const normalized = normalizeProjectLink(linkInput)
    if (links.includes(normalized)) {
      setLinkInputError('Эта ссылка уже добавлена')
      return
    }
    setLinks((prev) => [...prev, normalized])
    setLinkInput('')
    setLinkInputError(null)
  }

  const handleSaveTools = async () => {
    if (!projectId) return
    setSavingTools(true)
    setError(null)
    setSuccess(null)
    const tasks = tasksDraft
      .split('\n')
      .map((item) => item.trim())
      .filter((item, index, all) => Boolean(item) && all.indexOf(item) === index)
    try {
      const saved = await apiClient.updateProjectTools(projectId, {
        links,
        instructions: instructionsDraft,
        tasks,
        notes: notesDraft,
      })
      setToolsUpdatedAt(saved.updated_at || null)
      setSuccess('Инструменты проекта сохранены')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить инструменты проекта')
    } finally {
      setSavingTools(false)
    }
  }

  const handleUploadProjectDocs = async (files: FileList | null) => {
    if (!files || files.length === 0 || docsUploading) return
    setDocsUploading(true)
    setDocsError(null)
    try {
      const result = await apiClient.uploadDocuments(Array.from(files), projectId)
      const jobId = String(result?.item?.job_id || '').trim()
      if (jobId) setActiveJobId(jobId)
      await loadProjectDocs()
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Не удалось загрузить документы проекта')
    } finally {
      setDocsUploading(false)
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }

  const handleDeleteProjectDoc = async (docId: string) => {
    if (!docId || deletingDocId) return
    setDeletingDocId(docId)
    setDocsError(null)
    try {
      await apiClient.deleteDocument(docId)
      await loadProjectDocs()
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Не удалось удалить документ')
    } finally {
      setDeletingDocId(null)
    }
  }

  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-full p-4">
        <div className="glass-surface text-xs text-telegram-hint" style={{ padding: '14px 16px' }}>
          Некорректный идентификатор проекта
        </div>
      </div>
    )
  }

  return (
    <div className="project-page flex flex-col min-h-full">
      <div className="project-header sticky top-0 z-10 glass-header" style={{ padding: '10px 16px' }}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-bold flex items-center gap-2 truncate" style={{ fontSize: '14px' }}>
              <FolderKanban size={16} className="text-telegram-button" />
              {loading ? 'Проект' : (project?.name || 'Проект')}
            </h1>
            <p className="text-[10px] text-telegram-hint truncate">
              {project?.description || 'Ссылки, инструкции, задачи и общие документы проекта'}
            </p>
          </div>
          <button
            onClick={() => navigate('/projects')}
            className="rounded-lg glass-icon px-2 py-1.5 text-[10px] active:scale-95 transition-transform"
          >
            К списку
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: '12px 16px calc(12px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        {error && (
          <div className="glass-surface text-red-500 text-xs" style={{ padding: '10px 12px', marginBottom: '10px' }}>
            {error}
          </div>
        )}
        {success && (
          <div className="glass-surface text-green-600 text-xs" style={{ padding: '10px 12px', marginBottom: '10px' }}>
            {success}
          </div>
        )}

        <ProjectChatsSection
          loading={loading}
          contextMode={contextMode}
          conversations={conversations}
          onToggleContextMode={() => {
            setContextMode((prev) => (prev === 'space_shared' ? 'isolated' : 'space_shared'))
          }}
          onOpenNewChat={() => openProjectChat()}
          onOpenConversation={openProjectChat}
        />

        <div className="glass-surface" style={{ padding: '12px', marginBottom: '10px' }}>
          <p className="text-xs font-semibold mb-2 inline-flex items-center gap-1">
            <Link2 size={13} className="text-telegram-button" />
            Ссылки проекта
          </p>
          <div className="flex items-center gap-2 mb-1">
            <input
              value={linkInput}
              onChange={(event) => {
                setLinkInput(event.target.value)
                if (linkInputError) setLinkInputError(null)
              }}
              placeholder="Добавьте сайт (например, sudact.ru)"
              className="flex-1 glass-input text-xs"
              style={{ padding: '8px 10px' }}
            />
            <button
              onClick={handleAddLink}
              disabled={!linkInput.trim()}
              className="rounded-lg bg-telegram-button text-white px-2 py-1.5 text-[10px] font-semibold active:scale-95 transition-transform disabled:opacity-40"
            >
              Добавить
            </button>
          </div>
          {linkInputError && <p className="text-[10px] text-red-500 mb-1">{linkInputError}</p>}
          {links.length === 0 ? (
            <p className="text-[11px] text-telegram-hint">Ссылок пока нет</p>
          ) : (
            <div className="flex flex-col gap-1">
              {links.map((link) => (
                <div key={link} className="rounded-lg glass-icon px-2 py-1.5 flex items-center gap-2">
                  <a href={link} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 text-[11px] truncate text-telegram-link underline">
                    {link}
                  </a>
                  <button
                    onClick={() => setLinks((prev) => prev.filter((item) => item !== link))}
                    className="text-telegram-hint hover:text-red-500 active:scale-95 transition-transform"
                    title="Удалить ссылку"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass-surface" style={{ padding: '12px', marginBottom: '10px' }}>
          <p className="text-xs font-semibold mb-2">Инструкции проекта</p>
          <textarea
            value={instructionsDraft}
            onChange={(event) => setInstructionsDraft(event.target.value)}
            placeholder="Например: отвечай в формате мемо, с рисками и планом действий…"
            className="w-full glass-input text-xs resize-y"
            style={{ minHeight: '88px', padding: '8px 10px' }}
          />
        </div>

        <div className="glass-surface" style={{ padding: '12px', marginBottom: '10px' }}>
          <p className="text-xs font-semibold mb-2">Задачи проекта</p>
          <textarea
            value={tasksDraft}
            onChange={(event) => setTasksDraft(event.target.value)}
            placeholder={'Каждую среду — обзор новых судебных актов\nКаждый понедельник — проверка обновлений'}
            className="w-full glass-input text-xs resize-y"
            style={{ minHeight: '88px', padding: '8px 10px' }}
          />
          <p className="text-[10px] text-telegram-hint mt-1">По одной задаче на строку</p>
        </div>

        <div className="glass-surface" style={{ padding: '12px', marginBottom: '10px' }}>
          <p className="text-xs font-semibold mb-2">Заметки проекта</p>
          <textarea
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            placeholder="Общие заметки по кейсу…"
            className="w-full glass-input text-xs resize-y"
            style={{ minHeight: '88px', padding: '8px 10px' }}
          />
          <div className="mt-2 flex items-center justify-between">
            <p className="text-[10px] text-telegram-hint">
              {toolsUpdatedAt ? `Последнее обновление: ${new Date(toolsUpdatedAt).toLocaleString('ru-RU')}` : 'Инструменты ещё не сохранены'}
            </p>
            <button
              onClick={() => void handleSaveTools()}
              disabled={savingTools}
              className="rounded-lg bg-telegram-button text-white px-2.5 py-1.5 text-[10px] font-semibold inline-flex items-center gap-1 active:scale-95 transition-transform disabled:opacity-40"
            >
              {savingTools ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              Сохранить
            </button>
          </div>
        </div>

        <ProjectDocumentsSection
          projectId={projectId}
          docs={docs}
          docsLoading={docsLoading}
          docsUploading={docsUploading}
          docsError={docsError}
          deletingDocId={deletingDocId}
          activeJobId={activeJobId}
          acceptAttr={acceptAttr}
          maxUploadMb={maxUploadMb}
          uploadInputRef={uploadInputRef}
          onUploadProjectDocs={(files) => {
            void handleUploadProjectDocs(files)
          }}
          onRefreshDocs={() => {
            void loadProjectDocs()
          }}
          onDeleteDoc={(docId) => {
            void handleDeleteProjectDoc(docId)
          }}
        />
      </div>
    </div>
  )
}
